/**
 * PersonDrawerContent: body of the AdminDrawer (a Carbon SidePanel) for a
 * Person row.
 *
 * Mirrors OrgDrawerContent in shape (drawer-parts): related pivots, detail
 * grid, action list. The drawer shell (header, Close, slide-in) is owned by
 * AdminDrawer; this component is just the children.
 *
 * State ownership:
 *   - All mutations live in `useUsers` and are passed in by the parent so
 *     a single hook instance backs the list + drawer + nav badge.
 *   - Inline confirmation flows (role change, org reassign) are local: the
 *     parent doesn't need to know that the admin opened a sub-form.
 *   - `UpdateOrgConfirmDialog` is mounted INSIDE this component because the
 *     Platform-Admin threshold gate only matters when the drawer is open.
 *     Deactivate asks in a Carbon ConfirmDialog (danger) the same way.
 *
 * Voice:
 *   - Imperative action labels ("Change role", "Change organization", "Send
 *     welcome email", "Deactivate"), never noun phrases.
 *   - "Not yet welcomed" / "No organization" use named-subject form.
 *   - Failure toasts are editorial sentences ("Couldn't change the role.
 *     Try again."), never raw JSON.
 *   - The tenant-access stub names the next wave by name so the admin knows
 *     this is coming, not abandoned (Risk R9 in ORCHESTRATION-PLAN.md).
 *
 * Field-name notes vs. the spec prompt:
 *   - UserDto exposes `role` (a string like "GlobalAdmin"), not `roleType`.
 *     We render that string through `roleLabel()` for human display and use
 *     the raw value for transitions / mutation calls.
 *   - UserDto exposes `accessibleTenantCount` / `accessibleTenantIds`, not
 *     `assignedTenants` — we use the real names. Tenant *names* are not on
 *     the user row, so the read-only chip list shows the tenant IDs
 *     (acceptable Wave B stub; resolved when C-drawer wires `useTenants`).
 *   - Mutation arg keys are `objectId` (not `userId`); see useUsers.ts.
 */

import { useMemo, useState } from 'react'
import toast from 'react-hot-toast'

import { isHalfGrantedAdmin, type UserDto, type UseUsersResult } from '@/hooks/useUsers'
import type { TenantDto } from '@/hooks/useTenants'
import type { OrganizationListItemDto } from '@/lib/api'

import { Button } from '@/components/carbon/Button'
import { ConfirmDialog } from '@/components/carbon/ConfirmDialog'
import { InlineNotification } from '@/components/carbon/InlineNotification'
import { Select } from '@/components/carbon/Select'
import { Tag } from '@/components/carbon/Tag'
import PivotChip from '@/components/AdminPortal/PivotChip'
import {
  ActionList,
  ActionRow,
  DetailGrid,
  DrawerSection,
  InlinePanel,
  MonoId,
  Muted,
} from '@/components/AdminPortal/drawer-parts'
import { personDeactivateCopy } from '@/components/AdminPortal/confirm-copy'
import { formatLongDate, shortenId } from '@/components/AdminPortal/admin-format'
import PlatformAdminBadge from '@/components/UserManagement/PlatformAdminBadge'
import UpdateOrgConfirmDialog, {
  type UpdateOrgConfirmMode,
} from '@/components/UserManagement/UpdateOrgConfirmDialog'

/* -------------------------------------------------------------------------- */
/* Constants                                                                  */
/* -------------------------------------------------------------------------- */

// Mirrors IuvoGuardian.Data.OrganizationIds.IuvoPlatformOrgId. Hard-coded
// here for the same reason as in UserManagement.tsx — stable system constant,
// not tenant data, and the project has no central frontend constants file.
const PLATFORM_ORG_ID = '8d1e5b00-0000-4000-8000-000000000001'

// Mirrors IuvoGuardian.Core.Constants.OrganizationIds.IuvoMspOrgId — the org a
// user is moved to when their platform admin is revoked. Same hard-code rationale
// as PLATFORM_ORG_ID above.
const MSP_ORG_ID = '4e7ea580-fa6a-4047-ad66-ccce108348bc'

const ROLES: ReadonlyArray<{ value: string; label: string; description: string }> = [
  { value: 'Viewer', label: 'Viewer', description: 'Read-only dashboard access' },
  { value: 'Approver', label: 'Approver', description: 'Can approve or reject changes' },
  { value: 'ComplianceOfficer', label: 'Compliance Officer', description: 'Reads compliance reports' },
  { value: 'SecurityAdmin', label: 'Security Admin', description: 'Manages policies and data' },
  { value: 'GlobalAdmin', label: 'Global Admin', description: 'Full access, including user management' },
]

/* -------------------------------------------------------------------------- */
/* Props                                                                      */
/* -------------------------------------------------------------------------- */

export interface PersonDrawerContentProps {
  user: UserDto
  onClose(): void
  onPivot(target: 'organizations', orgId: string): void
  /**
   * Active orgs for the reassign picker. Optional: when the parent doesn't
   * pass it (e.g. orgs not yet loaded), the picker falls back to a disabled
   * select with an explanation. Acceptable Wave B stub.
   */
  availableOrgs?: OrganizationListItemDto[]
  /**
   * All monitored tenants (active + inactive). Drives the Manage-tenant-access
   * editor — the editor splits this into "currently assigned" vs "available
   * to add". When undefined the editor still mounts but renders an empty
   * state telling the admin to refresh.
   */
  availableTenants?: TenantDto[]
  // Mutation handles from useUsers. Inline-typed to match exactly what the
  // hook exports — the prompt's `ReturnType<typeof useUsers>` shape couples
  // the prop type to the hook's full surface, which is more than this
  // component needs.
  updateUserRole: UseUsersResult['updateUserRole']
  updateUserOrganization: UseUsersResult['updateUserOrganization']
  assignUserToTenant: UseUsersResult['assignUserToTenant']
  removeUserFromTenant: UseUsersResult['removeUserFromTenant']
  deactivateUser: UseUsersResult['deactivateUser']
  sendWelcomeEmail: UseUsersResult['sendWelcomeEmail']
  setPlatformAdmin: UseUsersResult['setPlatformAdmin']
}

/* -------------------------------------------------------------------------- */
/* Component                                                                  */
/* -------------------------------------------------------------------------- */

export default function PersonDrawerContent({
  user,
  onClose,
  onPivot,
  availableOrgs,
  availableTenants,
  updateUserRole,
  updateUserOrganization,
  assignUserToTenant,
  removeUserFromTenant,
  deactivateUser,
  sendWelcomeEmail,
  setPlatformAdmin,
}: PersonDrawerContentProps) {

  /* ---- Inline action states ------------------------------------------- */

  const [showRoleEditor, setShowRoleEditor] = useState(false)
  const [pendingRole, setPendingRole] = useState<string>(user.role)
  const [roleConfirm, setRoleConfirm] = useState<UpdateOrgConfirmMode | null>(null)
  const [isUpdatingRole, setIsUpdatingRole] = useState(false)

  const [showOrgEditor, setShowOrgEditor] = useState(false)
  const [pendingOrgId, setPendingOrgId] = useState<string>(user.organizationId ?? '')
  const [orgConfirm, setOrgConfirm] = useState<{
    mode: UpdateOrgConfirmMode
    targetOrgId: string
    targetOrgName: string
    fromOrgName: string
  } | null>(null)
  const [isUpdatingOrg, setIsUpdatingOrg] = useState(false)

  const [isSendingEmail, setIsSendingEmail] = useState(false)
  const [isDeactivating, setIsDeactivating] = useState(false)
  // Deactivate asks first; the ConfirmDialog stays open (busy) until the request settles.
  const [confirmDeactivate, setConfirmDeactivate] = useState(false)

  // Atomic Grant/Revoke Platform Admin — the sanctioned promote/demote path.
  // 'promote' = grant (move onto Platform org), 'demote' = revoke (move to MSP).
  const [platformAdminConfirm, setPlatformAdminConfirm] = useState<UpdateOrgConfirmMode | null>(null)
  const [isSettingPlatformAdmin, setIsSettingPlatformAdmin] = useState(false)

  const [showTenantEditor, setShowTenantEditor] = useState(false)
  // Per-tenant pending flag. Keyed by tenantId so multiple rows can be in
  // flight independently without the whole panel locking up.
  const [pendingTenantId, setPendingTenantId] = useState<string | null>(null)

  /* ---- Derived display values ----------------------------------------- */

  const lastLogin = useMemo(() => formatLongDate(user.lastLoginAt), [user.lastLoginAt])
  const createdAt = useMemo(() => formatLongDate(user.createdAt), [user.createdAt])
  const welcomeSent = useMemo(() => formatLongDate(user.welcomeEmailSentAt), [user.welcomeEmailSentAt])
  const shortObjectId = useMemo(() => shortenId(user.objectId), [user.objectId])

  /* ---- Role change ----------------------------------------------------- */

  const onRoleSubmit = () => {
    if (pendingRole === user.role) {
      setShowRoleEditor(false)
      return
    }
    // Role change can cross the Platform-Admin threshold only when the user
    // is already in the Platform org. Demote: was admin, new role is not
    // GlobalAdmin. Promote: in Platform org, becoming GlobalAdmin.
    const inPlatformOrg = user.organizationId === PLATFORM_ORG_ID
    const wasPlatformAdmin = user.isPlatformAdmin
    const willBePlatformAdmin = inPlatformOrg && pendingRole === 'GlobalAdmin'

    let mode: UpdateOrgConfirmMode | null = null
    if (!wasPlatformAdmin && willBePlatformAdmin) mode = 'promote'
    else if (wasPlatformAdmin && !willBePlatformAdmin) mode = 'demote'

    if (mode === null) {
      void commitRoleChange(pendingRole)
      return
    }
    setRoleConfirm(mode)
  }

  const commitRoleChange = async (role: string) => {
    setIsUpdatingRole(true)
    try {
      await updateUserRole({ objectId: user.objectId, role })
      toast.success('Role updated.')
      setShowRoleEditor(false)
      setRoleConfirm(null)
    } catch {
      // Editorial sentence — never the raw error JSON.
      toast.error("Couldn't change the role. Try again.")
    } finally {
      setIsUpdatingRole(false)
    }
  }

  /* ---- Org reassign ---------------------------------------------------- */

  const onOrgSubmit = () => {
    if (!pendingOrgId) return
    if (pendingOrgId === (user.organizationId ?? '')) {
      setShowOrgEditor(false)
      return
    }

    const targetOrg = (availableOrgs ?? []).find((o) => o.id === pendingOrgId)
    const targetOrgName = targetOrg?.name ?? 'the selected organization'
    const fromOrgName = user.organizationName ?? 'Unassigned'

    const wasPlatformAdmin = user.isPlatformAdmin
    const willBePlatformAdmin =
      pendingOrgId === PLATFORM_ORG_ID && user.role === 'GlobalAdmin'

    let mode: UpdateOrgConfirmMode | null = null
    if (!wasPlatformAdmin && willBePlatformAdmin) mode = 'promote'
    else if (wasPlatformAdmin && !willBePlatformAdmin) mode = 'demote'

    if (mode === null) {
      void commitOrgChange(pendingOrgId)
      return
    }
    setOrgConfirm({ mode, targetOrgId: pendingOrgId, targetOrgName, fromOrgName })
  }

  const commitOrgChange = async (organizationId: string) => {
    setIsUpdatingOrg(true)
    try {
      await updateUserOrganization({ objectId: user.objectId, organizationId })
      toast.success('Organization updated.')
      setShowOrgEditor(false)
      setOrgConfirm(null)
    } catch {
      toast.error("Couldn't reassign the organization. Try again.")
    } finally {
      setIsUpdatingOrg(false)
    }
  }

  /* ---- Welcome email -------------------------------------------------- */

  const handleSendWelcome = async () => {
    setIsSendingEmail(true)
    try {
      const result = await sendWelcomeEmail({ objectId: user.objectId })
      if (result.success) {
        toast.success(`Welcome email sent to ${result.sentTo}.`)
      } else {
        toast.error("Couldn't send the welcome email. Try again.")
      }
    } catch {
      toast.error("Couldn't send the welcome email. Try again.")
    } finally {
      setIsSendingEmail(false)
    }
  }

  /* ---- Deactivate ----------------------------------------------------- */

  const handleDeactivate = async () => {
    setIsDeactivating(true)
    try {
      await deactivateUser(user.objectId)
      toast.success(`${user.displayName} deactivated.`)
      onClose()
    } catch {
      toast.error("Couldn't deactivate the user. Try again.")
    } finally {
      setIsDeactivating(false)
      setConfirmDeactivate(false)
    }
  }

  /* ---- Grant / revoke Platform Admin (atomic) ------------------------- */

  // Org-name labels for the confirm dialog. Data-driven from the org picker
  // when loaded; falls back to the well-known names so the dialog still reads
  // correctly before Organizations has been opened.
  const platformOrgName =
    (availableOrgs ?? []).find((o) => o.id === PLATFORM_ORG_ID)?.name ?? 'iuvo Platform (internal)'
  const mspOrgName =
    (availableOrgs ?? []).find((o) => o.id === MSP_ORG_ID)?.name ?? 'iuvo, LLC'

  const commitPlatformAdmin = async (granted: boolean) => {
    setIsSettingPlatformAdmin(true)
    try {
      await setPlatformAdmin({ objectId: user.objectId, granted })
      toast.success(granted ? 'Platform admin granted.' : 'Platform admin revoked.')
      setPlatformAdminConfirm(null)
    } catch (e) {
      // setPlatformAdmin maps server errors to friendly sentences (e.g. the
      // self-revoke guard); surface that message rather than a generic one.
      toast.error(e instanceof Error ? e.message : "Couldn't change platform admin. Try again.")
    } finally {
      setIsSettingPlatformAdmin(false)
    }
  }

  /* ---- Render --------------------------------------------------------- */

  const hasOrg = !!user.organizationId
  const orgPivotLabel = user.organizationName ?? 'No organization'
  const anyBusy =
    isUpdatingRole || isUpdatingOrg || isSendingEmail || isDeactivating || isSettingPlatformAdmin

  return (
    <div className="flex flex-col gap-6">
      {/* ── Half-grant notice: name the verdict before the data ─────────── */}
      {isHalfGrantedAdmin(user) && <HalfGrantNotice user={user} />}

      {/* ── Related ─────────────────────────────────────────────────────── */}
      <DrawerSection title="Related" first>
        <DetailGrid
          items={[
            {
              label: 'Organization',
              value:
                hasOrg && user.organizationId ? (
                  <PivotChip
                    label={orgPivotLabel}
                    onClick={() => onPivot('organizations', user.organizationId as string)}
                  />
                ) : (
                  <Muted>No organization</Muted>
                ),
            },
            {
              label: 'Tenants',
              value:
                user.accessibleTenantCount === 0
                  ? <Muted>No tenants assigned</Muted>
                  : user.accessibleTenantCount === 1
                    ? '1 tenant assigned'
                    : `${user.accessibleTenantCount} tenants assigned`,
            },
            {
              label: 'Role',
              value: (
                <span className="flex flex-wrap items-center gap-2">
                  <Tag>{roleLabel(user.role)}</Tag>
                  <PlatformAdminBadge isPlatformAdmin={user.isPlatformAdmin} />
                </span>
              ),
            },
          ]}
        />
      </DrawerSection>

      {/* ── Detail fields ───────────────────────────────────────────────── */}
      <DrawerSection title="Details">
        <DetailGrid
          items={[
            { label: 'Email', value: user.email },
            { label: 'Object ID', value: <MonoId value={user.objectId} short={shortObjectId} /> },
            { label: 'Last login', value: lastLogin ?? <Muted>Never</Muted> },
            { label: 'Welcome status', value: welcomeSent ? `Sent ${welcomeSent}` : <Muted>Not yet welcomed</Muted> },
            { label: 'Created', value: createdAt ?? <Muted>Not recorded</Muted> },
            { label: 'Status', value: user.isActive ? 'Active' : <Muted>Inactive</Muted> },
          ]}
        />
      </DrawerSection>

      {/* ── Actions ─────────────────────────────────────────────────────── */}
      <DrawerSection title="Actions">
        <ActionList>
          {/* Grant / revoke Platform Admin: the atomic, sanctioned path. Sets
              org + role together so a half-applied admin can never be produced.
              Shown first because it's the primary way to (de)authorize iuvo staff. */}
          <ActionRow
            label={user.isPlatformAdmin ? 'Revoke iuvo platform admin' : 'Grant iuvo platform admin'}
            description={
              user.isPlatformAdmin
                ? `Sees and manages every customer. Revoking moves them to ${mspOrgName}.`
                : 'Platform-wide access to every customer tenant. Reserved for iuvo staff.'
            }
            onClick={() => setPlatformAdminConfirm(user.isPlatformAdmin ? 'demote' : 'promote')}
            disabled={anyBusy}
          />
        </ActionList>
      </DrawerSection>

      {/* Advanced: the raw role/org controls. These can produce a half-state
          (GlobalAdmin off the Platform org), so they stay guarded by the
          threshold confirm dialog and the notice at the top of the drawer.
          Prefer the atomic action above for routine grants. */}
      <DrawerSection title="Advanced">
        <ActionList>
          {/* Change role: inline editor */}
          {!showRoleEditor ? (
            <ActionRow
              label="Change role"
              description={`Currently ${roleLabel(user.role)}. Updates apply on next sign-in.`}
              onClick={() => {
                setPendingRole(user.role)
                setShowRoleEditor(true)
              }}
              disabled={anyBusy}
            />
          ) : (
            <InlinePanel
              heading="Change role"
              primaryLabel="Confirm role"
              primaryDisabled={isUpdatingRole || pendingRole === user.role}
              primaryLoading={isUpdatingRole}
              onPrimary={onRoleSubmit}
              onCancel={() => {
                setShowRoleEditor(false)
                setPendingRole(user.role)
              }}
              cancelDisabled={isUpdatingRole}
            >
              <Select
                label="Role"
                value={pendingRole}
                onChange={(e) => setPendingRole(e.target.value)}
                disabled={isUpdatingRole}
                options={ROLES.map((r) => ({
                  value: r.value,
                  label: `${r.label}${r.value === user.role ? ' (current)' : ''}`,
                }))}
              />
            </InlinePanel>
          )}

          {/* Change organization: inline picker */}
          {!showOrgEditor ? (
            <ActionRow
              label="Change organization"
              description={`Currently in ${user.organizationName ?? 'no organization'}.`}
              onClick={() => {
                setPendingOrgId(user.organizationId ?? '')
                setShowOrgEditor(true)
              }}
              disabled={anyBusy}
            />
          ) : (
            <InlinePanel
              heading="Change organization"
              primaryLabel="Confirm change"
              primaryDisabled={
                isUpdatingOrg ||
                !pendingOrgId ||
                pendingOrgId === (user.organizationId ?? '') ||
                !availableOrgs ||
                availableOrgs.length === 0
              }
              primaryLoading={isUpdatingOrg}
              onPrimary={onOrgSubmit}
              onCancel={() => {
                setShowOrgEditor(false)
                setPendingOrgId(user.organizationId ?? '')
              }}
              cancelDisabled={isUpdatingOrg}
            >
              {availableOrgs && availableOrgs.length > 0 ? (
                <Select
                  label="Organization"
                  value={pendingOrgId}
                  onChange={(e) => setPendingOrgId(e.target.value)}
                  disabled={isUpdatingOrg}
                >
                  <option value="" disabled>
                    Select an organization
                  </option>
                  {availableOrgs.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.name}
                      {o.id === PLATFORM_ORG_ID ? ' (Platform)' : ''}
                      {o.id === user.organizationId ? ' (current)' : ''}
                    </option>
                  ))}
                </Select>
              ) : (
                <Select
                  label="Organization"
                  disabled
                  helper="Open the Organizations tab once to load the list."
                  options={[{ value: '', label: 'The organization list has not loaded' }]}
                />
              )}
            </InlinePanel>
          )}

          {/* Manage tenant access: inline two-list editor */}
          {!showTenantEditor ? (
            <ActionRow
              label="Manage tenant access"
              description={
                user.accessibleTenantCount === 0
                  ? 'No tenants assigned.'
                  : `${user.accessibleTenantCount} tenant${user.accessibleTenantCount === 1 ? '' : 's'} assigned.`
              }
              onClick={() => setShowTenantEditor(true)}
              disabled={anyBusy}
            />
          ) : (
            <TenantAccessEditor
              user={user}
              availableTenants={availableTenants}
              pendingTenantId={pendingTenantId}
              onAdd={async (tenantId) => {
                setPendingTenantId(tenantId)
                try {
                  await assignUserToTenant({ objectId: user.objectId, tenantId })
                  toast.success('Tenant added.')
                } catch {
                  toast.error("Couldn't add the tenant. Try again.")
                } finally {
                  setPendingTenantId(null)
                }
              }}
              onRemove={async (tenantId) => {
                setPendingTenantId(tenantId)
                try {
                  await removeUserFromTenant({ objectId: user.objectId, tenantId })
                  toast.success('Tenant removed.')
                } catch {
                  toast.error("Couldn't remove the tenant. Try again.")
                } finally {
                  setPendingTenantId(null)
                }
              }}
              onClose={() => setShowTenantEditor(false)}
            />
          )}

          {/* Welcome email */}
          <ActionRow
            label={user.welcomeEmailSentAt ? 'Resend welcome email' : 'Send welcome email'}
            description={
              welcomeSent
                ? `Last sent ${welcomeSent}.`
                : 'They have not received the Guardian welcome yet.'
            }
            onClick={() => void handleSendWelcome()}
            disabled={anyBusy}
          />

          {/* Deactivate: only when active */}
          {user.isActive && (
            <ActionRow
              label="Deactivate"
              description="Block sign-in for this user. Reversible via support."
              onClick={() => setConfirmDeactivate(true)}
              disabled={anyBusy}
              danger
            />
          )}
        </ActionList>
      </DrawerSection>

      {/* ── Platform-Admin threshold confirm: ROLE TRANSITION ──────────── */}
      {roleConfirm && (
        <UpdateOrgConfirmDialog
          open={true}
          mode={roleConfirm}
          userEmail={user.email}
          fromOrganizationName={user.organizationName ?? 'Unassigned'}
          toOrganizationName={user.organizationName ?? 'Unassigned'}
          isPending={isUpdatingRole}
          onCancel={() => setRoleConfirm(null)}
          onConfirm={() => void commitRoleChange(pendingRole)}
        />
      )}

      {/* ── Platform-Admin threshold confirm: ORG TRANSITION ───────────── */}
      {orgConfirm && (
        <UpdateOrgConfirmDialog
          open={true}
          mode={orgConfirm.mode}
          userEmail={user.email}
          fromOrganizationName={orgConfirm.fromOrgName}
          toOrganizationName={orgConfirm.targetOrgName}
          isPending={isUpdatingOrg}
          onCancel={() => setOrgConfirm(null)}
          onConfirm={() => void commitOrgChange(orgConfirm.targetOrgId)}
        />
      )}

      {/* ── Atomic Grant / Revoke Platform Admin confirm ───────────────── */}
      {platformAdminConfirm && (
        <UpdateOrgConfirmDialog
          open={true}
          mode={platformAdminConfirm}
          userEmail={user.email}
          fromOrganizationName={user.organizationName ?? 'Unassigned'}
          toOrganizationName={platformAdminConfirm === 'promote' ? platformOrgName : mspOrgName}
          isPending={isSettingPlatformAdmin}
          onCancel={() => setPlatformAdminConfirm(null)}
          onConfirm={() => void commitPlatformAdmin(platformAdminConfirm === 'promote')}
        />
      )}

      {/* ── Deactivate confirm (danger) ────────────────────────────────── */}
      {confirmDeactivate && (
        <ConfirmDialog
          open
          {...personDeactivateCopy(user.displayName || user.email)}
          loading={isDeactivating}
          onConfirm={() => void handleDeactivate()}
          onCancel={() => setConfirmDeactivate(false)}
        />
      )}
    </div>
  )
}

/**
 * Half-grant notice: an advisor-voiced notice at the top of the drawer when
 * the person holds Global Admin but is not a platform admin (the "sees
 * nothing" shape). Names the verdict first, leads with the named subject, and
 * points at the fix. The neutral info tone, not Coral: the state is
 * recoverable and routine to resolve, not a blocking error.
 */
function HalfGrantNotice({ user }: { user: UserDto }) {
  const orgName = user.organizationName ?? 'no organization'
  const subject = user.displayName || user.email
  return (
    <InlineNotification kind="info" title="This grant was never finished.">
      {subject} holds Global Admin but sits in {orgName}, not the platform org, so they may have no
      platform-wide access. Use Grant iuvo platform admin below to finish it, or change their role.
    </InlineNotification>
  )
}

/* -------------------------------------------------------------------------- */
/* Subcomponents                                                              */
/* -------------------------------------------------------------------------- */

/**
 * Two-list tenant-access editor mounted inline inside the Person drawer, on
 * layer-02 like the other inline editors.
 *
 * Top list = tenants the user already has access to (Remove buttons).
 * Bottom list = tenants the user does NOT yet have access to (Add buttons).
 *
 * Each click is its own optimistic-free API call: the parent's `onAdd` /
 * `onRemove` fire the mutation, which invalidates `userKeys.all` on success,
 * which refetches the user list and re-renders this panel with the new split.
 * No local sync state needed.
 */
function TenantAccessEditor({
  user,
  availableTenants,
  pendingTenantId,
  onAdd,
  onRemove,
  onClose,
}: {
  user: UserDto
  availableTenants?: TenantDto[]
  pendingTenantId: string | null
  onAdd(tenantId: string): Promise<void>
  onRemove(tenantId: string): Promise<void>
  onClose(): void
}) {
  const assignedIds = useMemo(
    () => new Set(user.accessibleTenantIds),
    [user.accessibleTenantIds],
  )

  const { assigned, available } = useMemo(() => {
    const all = availableTenants ?? []
    const assigned = all.filter((t) => assignedIds.has(t.tenantId))
    const available = all
      .filter((t) => !assignedIds.has(t.tenantId) && t.isActive)
      .sort((a, b) => a.name.localeCompare(b.name))
    assigned.sort((a, b) => a.name.localeCompare(b.name))
    return { assigned, available }
  }, [availableTenants, assignedIds])

  // Cover the edge case where the user has explicit tenant IDs that don't
  // appear in the tenants list (deactivated, deleted, or list still loading).
  // Show them as raw IDs so the admin can still remove the orphan grant.
  const orphanIds = useMemo(() => {
    const knownIds = new Set((availableTenants ?? []).map((t) => t.tenantId))
    return user.accessibleTenantIds.filter((id) => !knownIds.has(id))
  }, [user.accessibleTenantIds, availableTenants])

  const isTenantsLoaded = availableTenants !== undefined

  return (
    <li role="group" aria-labelledby="tenant-access-heading" className="border-b border-line-subtle bg-layer-2 px-4 py-4">
      <div className="mb-3 flex items-center justify-between gap-4">
        <p id="tenant-access-heading" className="m-0 text-body font-semibold text-text-primary">
          Manage tenant access
        </p>
        <Button kind="ghost" size="sm" onClick={onClose}>
          Done
        </Button>
      </div>

      {!isTenantsLoaded ? (
        <p className="m-0 text-body text-text-helper">Loading the tenant list.</p>
      ) : (
        <div className="flex flex-col gap-4">
          {/* Currently assigned */}
          <div>
            <p className="m-0 mb-2 text-helper text-text-helper">
              Currently assigned ({assigned.length + orphanIds.length})
            </p>
            {assigned.length === 0 && orphanIds.length === 0 ? (
              <p className="m-0 text-body text-text-helper">No tenants assigned yet.</p>
            ) : (
              <ul className="m-0 list-none border-t border-line-subtle p-0">
                {assigned.map((t) => (
                  <TenantAccessRow
                    key={t.tenantId}
                    title={t.name}
                    subtitle={t.domain ?? t.tenantId}
                    pending={pendingTenantId === t.tenantId}
                    actionLabel="Remove"
                    onClick={() => void onRemove(t.tenantId)}
                    disabled={pendingTenantId !== null && pendingTenantId !== t.tenantId}
                  />
                ))}
                {orphanIds.map((id) => (
                  <TenantAccessRow
                    key={id}
                    title="Unknown tenant"
                    subtitle={id}
                    pending={pendingTenantId === id}
                    actionLabel="Remove"
                    onClick={() => void onRemove(id)}
                    disabled={pendingTenantId !== null && pendingTenantId !== id}
                  />
                ))}
              </ul>
            )}
          </div>

          {/* Available to add */}
          <div>
            <p className="m-0 mb-2 text-helper text-text-helper">
              Available to add ({available.length})
            </p>
            {available.length === 0 ? (
              <p className="m-0 text-body text-text-helper">
                {assigned.length > 0
                  ? 'This user already has access to every active tenant.'
                  : 'No active tenants in the system yet.'}
              </p>
            ) : (
              <ul className="m-0 max-h-[260px] list-none overflow-y-auto border-t border-line-subtle p-0">
                {available.map((t) => (
                  <TenantAccessRow
                    key={t.tenantId}
                    title={t.name}
                    subtitle={t.domain ?? t.tenantId}
                    pending={pendingTenantId === t.tenantId}
                    actionLabel="Add"
                    onClick={() => void onAdd(t.tenantId)}
                    disabled={pendingTenantId !== null && pendingTenantId !== t.tenantId}
                  />
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </li>
  )
}

function TenantAccessRow({
  title,
  subtitle,
  pending,
  actionLabel,
  onClick,
  disabled,
}: {
  title: string
  subtitle: string
  pending: boolean
  actionLabel: string
  onClick(): void
  disabled?: boolean
}) {
  return (
    <li className="flex items-center justify-between gap-2 border-b border-line-subtle py-2">
      <div className="min-w-0">
        <p className="m-0 truncate text-body font-semibold text-text-primary">{title}</p>
        <p className="m-0 truncate font-mono text-helper text-text-helper" title={subtitle}>
          {subtitle}
        </p>
      </div>
      <Button
        kind="ghost"
        size="sm"
        onClick={onClick}
        disabled={disabled}
        loading={pending}
        aria-label={`${actionLabel} ${title}`}
        className="flex-none"
      >
        {actionLabel}
      </Button>
    </li>
  )
}

/* -------------------------------------------------------------------------- */
/* Pure helpers                                                               */
/* -------------------------------------------------------------------------- */

function roleLabel(role: string): string {
  return ROLES.find((r) => r.value === role)?.label ?? role
}
